import { AutomaticBaseChangeSucceededEvent, ClosedEvent, CommentDeletedEvent, Commit, ConvertToDraftEvent, CrossReferencedEvent, HeadRefDeletedEvent, HeadRefForcePushedEvent, IssueTimelineItems, LabeledEvent, MergedEvent, PullRequestCommit, PullRequestReview, PullRequestReviewThread, PullRequestTimelineItems, ReadyForReviewEvent, ReferencedEvent, RenamedTitleEvent, ReopenedEvent, ReviewDismissedEvent, ReviewRequestedEvent, UnlabeledEvent } from '@octokit/graphql-schema';
import { CheckCircleIcon, GitCommitIcon, CrossReferenceIcon, EyeIcon, GitBranchIcon, GitMergeIcon, GitPullRequestClosedIcon, GitPullRequestDraftIcon, IssueClosedIcon, IssueReopenedIcon, PencilIcon, RepoPushIcon, SkipIcon, TagIcon, TrashIcon, XIcon } from '@primer/octicons-react';
import { useContext } from 'react';
import { useDateFormatter } from 'react-aria';
import { Button, Link } from 'react-aria-components';
import { mutate } from 'swr';
import { PullRequestContext, PullRequestPage } from './PullRequest';
import { CommentCard, CommentBody, Reactions } from './CommentCard';
import { Card, BranchName, GithubLabel, Icon, User, IssueStatus, Avatar, Status } from './components';
import { CommentForm } from './CommentForm';
import { graphql } from './client';
import { GitHubLink } from './SlideOver';

export function Timeline({items, onDeleteComment}: {items: (IssueTimelineItems | PullRequestTimelineItems | null)[], onDeleteComment?: (id: string) => Promise<void>}) {
  return <>
    {items.map((item, i) => {
      switch (item?.__typename) {
        case 'IssueComment':
          return <CommentCard key={i} data={item} onDelete={onDeleteComment ? () => onDeleteComment(item.id) : undefined} />;
        case 'AutomaticBaseChangeSucceededEvent':
          return <BaseChanged key={i} data={item} />;
        case 'PullRequestCommit':
          return <Committed key={i} data={item} />;
        case 'HeadRefForcePushedEvent':
          return <ForcePushed key={i} data={item} />;
        case 'PullRequestReview':
          return <Reviewed key={i} data={item} />;
        case 'ReviewDismissedEvent':
          return <ReviewDismissed key={i} data={item} />;
        case 'RenamedTitleEvent':
          return <Renamed key={i} data={item} />;
        case 'LabeledEvent':
          return <Labeled key={i} data={item} />;
        case 'UnlabeledEvent':
          return <Unlabeled key={i} data={item} />;
        case 'ClosedEvent':
          return <Closed key={i} data={item} />;
        case 'ReopenedEvent':
          return <Reopened key={i} data={item} />;
        case 'MergedEvent':
          return <Merged key={i} data={item} />;
        case 'HeadRefDeletedEvent':
          return <BranchDeleted key={i} data={item} />;
        case 'CrossReferencedEvent':
          return <CrossReferenced key={i} data={item} />;
        case 'ReferencedEvent':
          return <Referenced key={i} data={item} />;
        case 'ReviewRequestedEvent':
          return <ReviewRequested key={i} data={item} />;
        case 'ConvertToDraftEvent':
          return <ConvertToDraft key={i} data={item} />;
        case 'ReadyForReviewEvent':
          return <ReadyForReview key={i} data={item} />;
        case 'CommentDeletedEvent':
          return <CommentDeleted key={i} data={item} />;
        case 'MentionedEvent':
        case 'SubscribedEvent':
          return null;
        default:
          return <p key={i}>Unknown event <code className="break-all text-xs">{JSON.stringify(item)}</code></p>;
      }
    })}
  </>;
}

Timeline.issueFragment = () => `
fragment IssueTimelineFragment on PullRequestTimelineItems {
  __typename
  ...IssueCommentFragment
  ...RenamedTitleFragment
  ...LabeledEventFragment
  ...UnlabeledEventFragment
  ...ClosedEventFragment
  ...ReopenedEventFragment
  ...CrossReferencedEventFragment
  ...ReferencedEventFragment
  ...CommentDeletedEventFragment
}

${CommentCard.fragment}
${Renamed.fragment}
${Labeled.fragment}
${Unlabeled.fragment}
${Closed.fragment}
${Reopened.fragment}
${User.fragment}
${Reactions.fragment}
${CrossReferenced.fragment}
${Referenced.fragment}
${CommentDeleted.fragment}
`;

Timeline.pullRequestFragment = () => `
fragment PullRequestTimelineFragment on PullRequestTimelineItems {
  ...IssueTimelineFragment
  ...BaseChangeEventFragment
  ...CommittedEventFragment
  ...ForcePushedEventFragment
  ...ReviewedEventFragment
  ...ReviewDismissedFragment
  ...MergedEventFragment
  ...BranchDeletedEventFragment
  ...ReviewRequestedEventFragment
  ...ConvertToDraftEventFragment
  ...ReadyForReviewEventFragment
}

${Timeline.issueFragment()}
${BaseChanged.fragment}
${Committed.fragment}
${ForcePushed.fragment}
${Reviewed.fragment}
${ReviewDismissed.fragment}
${Merged.fragment}
${BranchDeleted.fragment}
${PullRequestThread.fragment}
${ReviewRequested.fragment}
${ConvertToDraft.fragment}
${ReadyForReview.fragment}
`;

function Labeled({data}: {data: LabeledEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><TagIcon /></Icon>
      <span><User actor={data.actor!} /> added the <GithubLabel color={data.label.color}>{data.label.name}</GithubLabel> label</span>
    </div>
  );
}

Labeled.fragment = `
fragment LabeledEventFragment on LabeledEvent {
  id
  actor {
    ...ActorFragment
  }
  label {
    name
    color
  }
}
`;

function Unlabeled({data}: {data: UnlabeledEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><TagIcon /></Icon>
      <span><User actor={data.actor!} /> removed the <GithubLabel color={data.label.color}>{data.label.name}</GithubLabel> label</span>
    </div>
  );
}

Unlabeled.fragment = `
fragment UnlabeledEventFragment on UnlabeledEvent {
  id
  actor {
    ...ActorFragment
  }
  label {
    name
    color
  }
}
`;

function Closed({data}: {data: ClosedEvent}) {
  return (
    <div className="flex items-center gap-2">
      {data.stateReason === "NOT_PLANNED" ? (
        <Icon className="bg-daw-gray-300 text-daw-gray-800"><SkipIcon /></Icon>
      ) : (
        <Icon className="bg-purple-600 text-white"><IssueClosedIcon /></Icon>
      )}
      <span><User actor={data.actor!} /> closed this as {data.stateReason?.toLowerCase()}</span>
    </div>
  );
}

Closed.fragment = `
fragment ClosedEventFragment on ClosedEvent {
  id
  actor {
    ...ActorFragment
  }
  stateReason
}
`;

function Reopened({data}: {data: ReopenedEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-green-600 text-white"><IssueReopenedIcon /></Icon>
      <span><User actor={data.actor!} /> reopened this</span>
    </div>
  );
}

Reopened.fragment = `
fragment ReopenedEventFragment on ReopenedEvent {
  id
  actor {
    ...ActorFragment
  }
}
`;

function Merged({data}: {data: MergedEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-purple-600 text-white"><GitMergeIcon /></Icon>
      <span><User actor={data.actor!} /> merged commit <CommitLink commit={data.commit!} /> into <BranchName>{data.mergeRefName}</BranchName></span>
    </div>
  );
}

Merged.fragment = `
fragment MergedEventFragment on MergedEvent {
  id
  actor {
    ...ActorFragment
  }
  commit {
    url
    abbreviatedOid
  }
  mergeRefName
}
`;

function CommitLink({commit}: {commit: Commit}) {
  return <GitHubLink href={commit.url} className="text-xs hover:underline"><code>{commit.abbreviatedOid}</code></GitHubLink>;
}

function BranchDeleted({data}: {data: HeadRefDeletedEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><GitBranchIcon /></Icon>
      <span><User actor={data.actor!} /> deleted the <BranchName>{data.headRefName}</BranchName> branch</span>
    </div>
  );
}

BranchDeleted.fragment = `
fragment BranchDeletedEventFragment on HeadRefDeletedEvent {
  id
  actor {
    ...ActorFragment
  }
  headRefName
}
`;

function Renamed({data}: {data: RenamedTitleEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><PencilIcon /></Icon>
      <span><User actor={data.actor!} /> changed the title from <del>{data.previousTitle}</del> to <ins>{data.currentTitle}</ins></span>
    </div>
  );
}

Renamed.fragment = `
fragment RenamedTitleFragment on RenamedTitleEvent {
  id
  actor {
    ...ActorFragment
  }
  previousTitle
  currentTitle
}
`;

function Reviewed({data}: {data: PullRequestReview}) {
  let pr = useContext(PullRequestContext);
  let threadsById = new Map();
  for (let thread of pr?.reviewThreads.nodes!) {
    threadsById.set(thread?.comments.nodes?.[0]?.id, thread);
  }

  return (
    <div
      className="grid items-center gap-2"
      style={{
        gridTemplateAreas: `
          "icon description"
          ".    issue"
        `,
        gridTemplateColumns: 'min-content 1fr'
      }}>
      {data.state === 'CHANGES_REQUESTED'
        ? <Icon className="bg-red-600 text-white"><GitPullRequestClosedIcon /></Icon>
        : data.state === 'APPROVED'
        ? <Icon className="bg-green-600 text-white"><CheckCircleIcon /></Icon>
        : <Icon className="bg-daw-gray-300 text-daw-gray-800"><EyeIcon /></Icon>}
      <div style={{gridArea: 'description'}}><User actor={data.author!} />  {data.state === 'CHANGES_REQUESTED' ? 'requested changes' : data.state === 'APPROVED' ? 'approved' : 'reviewed'}</div>
      {(data.body || !!data.comments?.nodes?.length) && (
        <div style={{gridArea: 'issue'}} className="flex flex-col gap-2">
          {data.body &&
            <Card>
              <CommentBody>{data.body}</CommentBody>
            </Card>
          }
          {data.comments.nodes?.map(comment => {
            let thread = threadsById.get(comment?.id);
            if (thread) {
              return <PullRequestThread key={thread.id} data={thread} />;
            }
          })}
        </div>
      )}
    </div>
  );
}

Reviewed.fragment = `
fragment ReviewedEventFragment on PullRequestReview {
  id
  author {
    ...ActorFragment
  }
  body
  createdAt
  reactionGroups {
    ...ReactionFragment
  }
  state
  comments(first:100) {
    nodes {
      id
    }
  }
}
`;

export function PullRequestThread({data}: {data: PullRequestReviewThread}) {
  let df = useDateFormatter({
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });
  let pr = useContext(PullRequestContext)!;

  async function resolveThread() {
    await graphql(`
      mutation ResolveReviewThread($threadId: ID!) {
        resolveReviewThread(input: {threadId: $threadId}) {
          thread { id isResolved }
        }
      }
    `, { threadId: data.id });
    await mutate([PullRequestPage.query(), { owner: pr.repository.owner.login, repo: pr.repository.name, number: pr.number }]);
  }

  async function deleteReviewComment(id: string) {
    await graphql(`
      mutation DeletePullRequestReviewComment($id: ID!) {
        deletePullRequestReviewComment(input: {id: $id}) { clientMutationId }
      }
    `, { id });
    await mutate([PullRequestPage.query(), { owner: pr.repository.owner.login, repo: pr.repository.name, number: pr.number }]);
  }

  return (
    <Card>
      <details open={!data.isCollapsed}>
        <summary className="font-mono text-xs cursor-default">
          {data.path}
        </summary>
        <div className="flex flex-col gap-4 pt-3 mt-3 border-t border-daw-gray-200 whitespace-normal text-sm">
          {data.comments.nodes?.map(comment => (
            <div key={comment!.id} className="flex flex-col gap-2 pb-4 border-b border-daw-gray-200 last:border-b-0 last:pb-0">
              <div className="flex justify-between items-center">
                <div>
                  <User actor={comment!.author!} />
                  {' • '}
                  <span className="text-xs text-daw-gray-600">{df.format(new Date(comment!.createdAt))}</span>
                  {comment!.state === 'PENDING' &&
                    <span className="w-fit ml-2 px-2 py-0.5 rounded border text-xs font-medium bg-daw-yellow-100 border-daw-yellow-200 text-daw-yellow-700">pending</span>
                  }
                </div>
                {comment!.viewerCanDelete &&
                  <Button onPress={() => deleteReviewComment(comment!.id)} className="text-daw-gray-400 hover:text-daw-red-500 pressed:text-daw-red-600 cursor-default outline-none focus-visible:ring-2 ring-blue-600 rounded">
                    <TrashIcon size={14} />
                  </Button>
                }
              </div>
              <div>
                <CommentBody>{comment!.body}</CommentBody>
              </div>
              {comment!.reactionGroups && <Reactions id={comment!.id} data={comment!.reactionGroups} />}
            </div>
          ))}
        </div>
      </details>
    </Card>
  );
}

PullRequestThread.fragment = `
fragment PullRequestThreadFragment on PullRequestReviewThread {
  id
  isCollapsed
  isOutdated
  isResolved
  resolvedBy {
    ...ActorFragment
  }
  line
  diffSide
  path
  viewerCanResolve
  comments(first:100) {
    nodes {
      id
      author {
        ...ActorFragment
      }
      body
      createdAt
      reactionGroups {
        ...ReactionFragment
      }
      isMinimized
      minimizedReason
      startLine
      line
      path
      state
      outdated
      viewerCanDelete
    }
  }
}
`;

function ReviewDismissed({data}: {data: ReviewDismissedEvent}) {
  return (
    <div
      className="grid items-center gap-2"
      style={{
        gridTemplateAreas: `
          "icon description"
          ".    issue"
        `,
        gridTemplateColumns: 'min-content 1fr'
      }}>
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><XIcon /></Icon>
      <span><User actor={data.actor!} /> dismissed <Link href={data.review!.author!.url} target="_blank" className="font-semibold hover:underline">{data.review!.author!.login}</Link>'s stale review {data.pullRequestCommit && <>via <CommitLink commit={data.pullRequestCommit.commit} /></>}</span>
      {data.dismissalMessage && (
        <Card gridArea="issue">
          <CommentBody>{data.dismissalMessage}</CommentBody>
        </Card>
      )}
    </div>
  );
}

ReviewDismissed.fragment = `
fragment ReviewDismissedFragment on ReviewDismissedEvent {
  id
  actor {
    ...ActorFragment
  }
  review {
    author {
      login
      url
    }
  }
  dismissalMessage
  pullRequestCommit {
    commit {
      url
      abbreviatedOid
    }
  }
}
`;

function CrossReferenced({data}: {data: CrossReferencedEvent}) {
  return (
    <div
      className="grid items-center gap-2"
      style={{
        gridTemplateAreas: `
          "icon description"
          ".    issue"
        `,
        gridTemplateColumns: 'min-content 1fr'
      }}>
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><CrossReferenceIcon /></Icon>
      <span><User actor={data.actor!} /> referenced this</span>
      <Card gridArea="issue">
        <div className="flex gap-1 items-center">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <GitHubLink href={data.source.url} className="truncate font-semibold outline-none hover:underline focus-visible:underline">{data.source.title}</GitHubLink>
            {data.isCrossRepository &&
              <div className="flex gap-1 items-center">
                <Avatar src={data.source.repository.owner.avatarUrl} />
                <GitHubLink href={data.source.url} className="text-daw-gray-700 outline-none hover:underline focus-visible:underline">{data.source.repository.owner.login}/{data.source.repository.name} #{data.source.number}</GitHubLink>
              </div>
            }
            {!data.isCrossRepository && <GitHubLink href={data.source.url} className="text-daw-gray-700 outline-none hover:underline focus-visible:underline">#{data.source.number}</GitHubLink>}
          </div>
          <IssueStatus data={data.source} />
        </div>
      </Card>
    </div>
  );
}

CrossReferenced.fragment = `
fragment CrossReferencedEventFragment on CrossReferencedEvent {
  id
  actor {
    ...ActorFragment
  }
  isCrossRepository
  source {
    __typename
    ...on Issue {
      title
      number
      url
      number
      repository {
        name
        url
        owner {
          login
          avatarUrl
        }
      }
      state
    }
    ...on PullRequest {
      title
      number
      url
      number
      repository {
        name
        url
        owner {
          login
          avatarUrl
        }
      }
      state
    }
  }
}
`;

function Referenced({data}: {data: ReferencedEvent}) {
  return (
    <div
      className="grid items-center gap-2"
      style={{
        gridTemplateAreas: `
          "icon description"
          ".    commit"
        `,
        gridTemplateColumns: 'min-content 1fr'
      }}>
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><CrossReferenceIcon /></Icon>
      <span><User actor={data.actor!} /> referenced this pull request</span>
      <div style={{gridArea: 'commit'}} className="flex gap-2">
        <span className="flex-1 line-clamp-2 text-sm">
          <GitHubLink href={data.commit!.commitUrl} className="hover:underline">{data.commit!.message}</GitHubLink>
        </span>
        {data.commit!.statusCheckRollup && <Status state={data.commit!.statusCheckRollup.state} />}
        <CommitLink commit={data.commit!} />
      </div>
    </div>
  );
}

Referenced.fragment = `
fragment ReferencedEventFragment on ReferencedEvent {
  id
  actor {
    ...ActorFragment
  }
  isCrossRepository
  commit {
    url
    abbreviatedOid
    message
    commitUrl
    author {
      user {
        login
      }
      avatarUrl
    }
    statusCheckRollup {
      state
    }
  }
}
`;

function Committed({data}: {data: PullRequestCommit}) {
  return (
    <div className="flex gap-2 items-center">
      <Icon className="text-daw-gray-800"><GitCommitIcon /></Icon>
      <Avatar src={data.commit.author!.avatarUrl} />
      <span className="flex-1 line-clamp-2 text-sm">
        <GitHubLink href={data.commit.commitUrl} className="hover:underline">{data.commit.message}</GitHubLink>
      </span>
      {data.commit.statusCheckRollup && <Status state={data.commit.statusCheckRollup.state} />}
      <CommitLink commit={data.commit} />
    </div>
  );
}

Committed.fragment = `
fragment CommittedEventFragment on PullRequestCommit {
  id
  commit {
    url
    abbreviatedOid
    message
    commitUrl
    author {
      user {
        login
      }
      avatarUrl
    }
    statusCheckRollup {
      state
    }
  }
}
`;

function ForcePushed({data}: {data: HeadRefForcePushedEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><RepoPushIcon /></Icon>
      <span><User actor={data.actor!} /> force-pushed the {data.ref && <BranchName>{data.ref.name}</BranchName>} branch from <CommitLink commit={data.beforeCommit!} /> to <CommitLink commit={data.afterCommit!} /></span>
    </div>
  );
}

ForcePushed.fragment = `
fragment ForcePushedEventFragment on HeadRefForcePushedEvent {
  id
  actor {
    ...ActorFragment
  }
  beforeCommit {
    url
    abbreviatedOid
  }
  afterCommit {
    url
    abbreviatedOid
  }
  ref {
    name
  }
}
`;

function BaseChanged({data}: {data: AutomaticBaseChangeSucceededEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-green-600 text-white"><GitBranchIcon /></Icon>
      <span>Base automatically changed from <BranchName>{data.oldBase}</BranchName> to <BranchName>{data.newBase}</BranchName></span>
    </div>
  );
}

BaseChanged.fragment = `
fragment BaseChangeEventFragment on AutomaticBaseChangeSucceededEvent {
  id
  newBase
  oldBase
}
`;

function ReviewRequested({data}: {data: ReviewRequestedEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-green-600 text-white"><EyeIcon /></Icon>
      <span><User actor={data.actor!} /> requested a review{'login' in data.requestedReviewer! && <> from <Link href={data.requestedReviewer!.url} target="_blank" className="hover:underline font-semibold">{data.requestedReviewer!.login}</Link></>}</span>
    </div>
  );
}

ReviewRequested.fragment = `
fragment ReviewRequestedEventFragment on ReviewRequestedEvent {
  actor {
    ...ActorFragment
  }
  requestedReviewer {
    ...on Actor {
      login
      url
    }
  }
}
`;

function ConvertToDraft({data}: {data: ConvertToDraftEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><GitPullRequestDraftIcon /></Icon>
      <span><User actor={data.actor!} /> marked this pull request as a draft</span>
    </div>
  );
}

ConvertToDraft.fragment = `
fragment ConvertToDraftEventFragment on ConvertToDraftEvent {
  actor {
    ...ActorFragment
  }
}
`;

function ReadyForReview({data}: {data: ReadyForReviewEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><EyeIcon /></Icon>
      <span><User actor={data.actor!} /> marked this pull request as ready for review</span>
    </div>
  );
}

ReadyForReview.fragment = `
fragment ReadyForReviewEventFragment on ReadyForReviewEvent {
  actor {
    ...ActorFragment
  }
}
`;

function CommentDeleted({data}: {data: CommentDeletedEvent}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="bg-daw-gray-300 text-daw-gray-800"><XIcon /></Icon>
      <span><User actor={data.actor!} /> deleted a comment from <Link href={data.deletedCommentAuthor!.url} target="_blank" className="hover:underline font-semibold">{data.deletedCommentAuthor!.login}</Link></span>
    </div>
  );
}

CommentDeleted.fragment = `
fragment CommentDeletedEventFragment on CommentDeletedEvent {
  actor {
    ...ActorFragment
  }
  deletedCommentAuthor {
    login
    url
  }
}
`;
